/**
 * ConfigRelationSelect - Autocomplete component for selecting related configs
 * Used for parentConfigs, relatedConfigs, duplicateConfigs, dependsUpon fields
 */

import React, { useState, useCallback, useMemo, useEffect, useRef } from 'react';
import {
  Autocomplete,
  TextField,
  Chip,
  CircularProgress,
  Box,
  Typography,
} from '@mui/material';
import { debounce } from 'lodash';
import { useGlobalConfig } from 'context/GlobalContext';
import { ConfigRegistryApis } from '../api/configRegistryAPI';
import { ConfigRelation, ConfigRegistryItem } from '../types/configRegistryTypes';

interface ConfigRelationSelectProps {
  label?: string; // Optional - can use FieldLabel component instead
  value: ConfigRelation[] | ConfigRelation | null;
  onChange: (value: ConfigRelation[] | ConfigRelation | null) => void;
  disabled?: boolean;
  helperText?: string;
  excludeDocumentId?: string; // Exclude current config from search results
  multiple?: boolean; // Default: true (multi-select)
}

interface ConfigOption {
  documentId: string;
  configType: string;
  configKey: string;
  displayName: string;
  label: string; // For display in dropdown
}

export const ConfigRelationSelect: React.FC<ConfigRelationSelectProps> = ({
  label,
  value,
  onChange,
  disabled = false,
  helperText,
  excludeDocumentId,
  multiple = true,
}) => {
  const { rc } = useGlobalConfig();
  const [inputValue, setInputValue] = useState('');
  const [options, setOptions] = useState<ConfigOption[]>([]);
  const [loading, setLoading] = useState(false);

  // Track mounted state to prevent state updates after unmount
  const isMountedRef = useRef(true);

  // Convert ConfigRelation to ConfigOption for display
  const selectedOptions: ConfigOption[] | ConfigOption | null = useMemo(() => {
    if (multiple) {
      // Multi-select mode
      const arrayValue = Array.isArray(value) ? value : [];
      return arrayValue.map((rel) => ({
        documentId: rel.documentId || '',
        configType: rel.configType,
        configKey: rel.configKey,
        displayName: '',
        label: `${rel.configType}::${rel.configKey}`,
      }));
    } else {
      // Single-select mode
      if (!value || Array.isArray(value)) return null;
      return {
        documentId: value.documentId || '',
        configType: value.configType,
        configKey: value.configKey,
        displayName: '',
        label: `${value.configType}::${value.configKey}`,
      };
    }
  }, [value, multiple]);

  // Debounced search function with mounted check to prevent state updates after unmount
  const searchConfigs = useMemo(
    () =>
      debounce(async (searchTerm: string) => {
        if (!searchTerm || searchTerm.length < 2) {
          if (isMountedRef.current) {
            setOptions([]);
          }
          return;
        }

        if (isMountedRef.current) {
          setLoading(true);
        }
        try {
          const response = await ConfigRegistryApis.getConfigs(rc, {
            search: searchTerm,
            pageSize: 20,
          });

          // Check if component is still mounted before updating state
          if (!isMountedRef.current) return;

          const configs = (response.data as any)?.data || response.data || [];
          const configOptions: ConfigOption[] = configs
            .filter((c: ConfigRegistryItem) => c.documentId !== excludeDocumentId)
            .map((c: ConfigRegistryItem) => ({
              documentId: c.documentId,
              configType: c.configType,
              configKey: c.configKey,
              displayName: c.displayName,
              label: `${c.configType}::${c.configKey}`,
            }));

          setOptions(configOptions);
        } catch (error) {
          console.error('Failed to search configs:', error);
          if (isMountedRef.current) {
            setOptions([]);
          }
        } finally {
          if (isMountedRef.current) {
            setLoading(false);
          }
        }
      }, 300),
    [rc, excludeDocumentId]
  );

  // Cleanup: cancel debounced function and mark as unmounted
  useEffect(() => {
    isMountedRef.current = true;
    return () => {
      isMountedRef.current = false;
      searchConfigs.cancel();
    };
  }, [searchConfigs]);

  const handleInputChange = (_event: React.SyntheticEvent, newInputValue: string) => {
    setInputValue(newInputValue);
    searchConfigs(newInputValue);
  };

  const handleChange = (_event: React.SyntheticEvent, newValue: ConfigOption[] | ConfigOption | null) => {
    if (multiple) {
      // Multi-select mode
      const arrayValue = Array.isArray(newValue) ? newValue : [];
      const relations: ConfigRelation[] = arrayValue.map((opt) => ({
        documentId: opt.documentId,
        configType: opt.configType,
        configKey: opt.configKey,
      }));
      onChange(relations);
    } else {
      // Single-select mode
      if (!newValue || Array.isArray(newValue)) {
        onChange(null);
      } else {
        const relation: ConfigRelation = {
          documentId: newValue.documentId,
          configType: newValue.configType,
          configKey: newValue.configKey,
        };
        onChange(relation);
      }
    }
  };

  // Check if option is already selected
  const isOptionEqualToValue = (option: ConfigOption, val: ConfigOption) => {
    return option.configType === val.configType && option.configKey === val.configKey;
  };

  return (
    <Autocomplete
      multiple={multiple}
      value={selectedOptions}
      onChange={handleChange}
      inputValue={inputValue}
      onInputChange={handleInputChange}
      options={options}
      getOptionLabel={(option) => option.label}
      isOptionEqualToValue={isOptionEqualToValue}
      loading={loading}
      disabled={disabled}
      filterOptions={(x) => x} // Disable built-in filtering, use server-side search
      size="small"
      renderInput={(params) => (
        <TextField
          {...params}
          label={label}
          helperText={helperText}
          placeholder={disabled ? '' : 'Type to search configs...'}
          size="small"
          InputProps={{
            ...params.InputProps,
            endAdornment: (
              <>
                {loading && <CircularProgress color="inherit" size={20} />}
                {params.InputProps.endAdornment}
              </>
            ),
          }}
          sx={{
            '& .MuiInputBase-root': {
              bgcolor: 'background.paper',
              '&.Mui-disabled': {
                bgcolor: 'action.hover',
                '& .MuiInputBase-input': {
                  color: 'text.primary',
                  WebkitTextFillColor: 'unset',
                },
              },
            },
            '& .MuiOutlinedInput-root': {
              '&:hover .MuiOutlinedInput-notchedOutline': {
                borderColor: 'primary.main',
              },
            },
          }}
        />
      )}
      renderOption={(props, option) => (
        <Box
          component="li"
          {...props}
          key={option.documentId || option.label}
          sx={{
            py: 1.5,
            px: 2,
            borderBottom: '1px solid',
            borderColor: 'divider',
            '&:last-child': { borderBottom: 'none' },
            '&:hover': {
              bgcolor: 'action.hover',
            },
            '&.Mui-focused': {
              bgcolor: 'action.selected',
            },
          }}
        >
          <Box sx={{ width: '100%' }}>
            <Typography variant="body2" fontWeight={500} sx={{ mb: 0.25 }}>
              {option.label}
            </Typography>
            {option.displayName && (
              <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
                {option.displayName}
              </Typography>
            )}
          </Box>
        </Box>
      )}
      renderTags={(tagValue, getTagProps) =>
        tagValue.map((option, index) => (
          <Chip
            {...getTagProps({ index })}
            key={option.label}
            label={option.label}
            size="small"
            color="primary"
            variant="outlined"
            sx={{
              maxWidth: 250,
              borderRadius: '6px',
              fontWeight: 500,
              '&.Mui-disabled': {
                opacity: 1,
                bgcolor: 'grey.100',
                color: 'text.primary',
                border: '1px solid',
                borderColor: 'grey.400',
                '& .MuiChip-deleteIcon': { display: 'none' },
              },
            }}
          />
        ))
      }
      sx={{
        '& .MuiAutocomplete-popupIndicator': {
          color: 'text.secondary',
        },
        '& .MuiAutocomplete-clearIndicator': {
          color: 'text.secondary',
        },
      }}
      noOptionsText={inputValue.length < 2 ? 'Type at least 2 characters to search' : 'No configs found'}
    />
  );
};

